/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { ETextStyleVariant, Text } from '@outdoorsyco/bonfire';
import clsx from 'clsx';
import escapeHTML from 'escape-html';
import kebabCase from 'lodash/kebabCase';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import SuperhostBadgeLightSvg from '@/assets/superhost/superhost-badge-light.svg';
import InlineList from '@/components/common/InlineList/InlineList';
import Rating from '@/components/route/listing/Rating/Rating';
import Heading from '@/components/switchback/Heading/Heading';
import Link, { LinkVariants } from '@/components/switchback/Link/Link';
import Price, { PriceSize } from '@/components/switchback/Price';
import { ListingTile as GeneralPurposeListingTile } from '@/components/ui/GeneralPurposeListingTile';
import {
  ModularListingTile,
  ModularListingTileContent,
  ModularListingTileMedia,
} from '@/components/ui/ModularListingTile';
import { ModularSmallHorizontalListingTileContent } from '@/components/ui/ModularListingTile/ModularSmallHorizontalListingTile/ModularSmallHorizontalListingTileContent';
import { ModularSmallHorizontalListingTileMedia } from '@/components/ui/ModularListingTile/ModularSmallHorizontalListingTile/ModularSmallHorizontalListingTileMedia';
import ResponsiveImage from '@/components/utility/ResponsiveImage/ResponsiveImage';
import { TCurrency } from '@/config/locales';
import { EPage, PAGE_PATHNAME } from '@/constants/pages';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useRouter } from '@/hooks/useRouter';
import { EListingSource } from '@/services/analytics/listings/types';
import { OptimizelyFlags, useExperimentIsEnabled } from '@/services/experiments';
import { trackEvent } from '@/services/track-event';
import { IPrice, ISearchResultTile } from '@/utility/mapSearchResultToTile';

type MapSource = 'map-multi-marker' | 'map-single-marker';
export interface IProps {
  id?: number;
  title: string;
  href: string;
  image: {
    alt: string;
    src: string;
  };
  year: string;
  classText: string;
  length: string;
  rating?: {
    value: number;
    quantity: number;
  };
  infos: string[];
  isInstantBook: boolean;
  isUnavailable: boolean;
  isSuperhost: boolean;
  currency: TCurrency;
  price: IPrice;
  onClick?: () => void;
  source?: MapSource;
  isStay?: boolean;
  isCampground?: boolean;
  isOnWishlist?: boolean;
  index?: number;
  isGroup?: boolean;
  isFavorite?: boolean;
  rental: ISearchResultTile;
}

const MapListing: React.FC<IProps> = ({
  id,
  title,
  href,
  image,
  year,
  classText,
  length,
  rating,
  infos,
  isInstantBook,
  isUnavailable,
  isSuperhost,
  currency,
  price,
  onClick,
  source,
  isStay,
  isCampground,
  isOnWishlist,
  index,
  isGroup,
  isFavorite,
  rental,
}) => {
  const intl = useIntl();
  const { isAboveDesktop } = useBreakpoint();
  const superhostBadgeEnabled = useExperimentIsEnabled(OptimizelyFlags.SUPERHOST_BADGE);
  const listingTileUpdateEnabled = useExperimentIsEnabled(OptimizelyFlags.LISTING_TILE_UPDATE_V2);
  const testId = useMemo(() => `${kebabCase(title)}-card`, [title]);
  const joinedInfos = infos.join(', ');
  const infoList = rating ? [`(${rating.quantity})`, joinedInfos] : [joinedInfos];
  const inlineListClasses = rating ? 'first:hidden sm:first:inline' : '';
  const linkTitle = title && escapeHTML(title);
  const hasDiscount = isCampground
    ? price.currentPrice < (price.overrideDayPrice ?? 0)
    : price.currentPrice < (price.originalPrice ?? 0);

  const router = useRouter();
  const isEscapesSearchPage = router?.pathname?.includes(PAGE_PATHNAME[EPage.ESCAPES_SEARCH]);

  const onClickLink = (source: string | undefined) => {
    if (source) {
      trackEvent({
        event: 'click/card/rental',
        source: source,
      });
    }
    onClick && onClick();
  };
  const descriptionList = isCampground
    ? []
    : isStay
      ? [classText]
      : [`${year} ${classText} `, length];

  const rentalTile = {
    id: id || 0,
    title: title,
    linkUrl: href,
    photoUrl: image.src,
    photoAlt: image.alt,
    loading: false,
    isStay: isStay,
    isCampsite: false,
    isCampground: isCampground,
    instantBook: isInstantBook,
    unavailable: isUnavailable,
    currency: currency,
    price: price,
    isFavorite: isFavorite,
    details: {
      year: year,
      classText: classText,
      feet: length,
      ratingNumber: rating?.value,
      ratingQtd: rating?.quantity,
      infos: infos,
    },
  };

  if (isEscapesSearchPage) {
    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events
      <article
        data-testid={testId}
        className="flex items-center h-32 bg-primary-contrast rounded-md"
        data-instantbook={String(isInstantBook)}
        data-unavailable={String(isUnavailable)}
        onClick={() => onClickLink(source)}>
        <Link
          naked
          aria-label={linkTitle}
          className="flex autoType400 hover:no-underline"
          href={href}
          target={isAboveDesktop ? '_blank' : undefined}
          variant={LinkVariants.noUnderline}
          title={linkTitle}>
          <>
            <div className="relative w-32 h-32 my-auto mr-2 overflow-clip shrink-0 rounded-l-md">
              <ResponsiveImage
                // className="absolute w-full h-full top-1/2 left-1/2 transform -translate-y-1/2 -translate-x-1/2"
                className="w-full h-full"
                src={image.src}
                role="presentation"
                alt={image.alt}
                sourceSizes={['landscape280']}
                sizes="144px"
              />
            </div>
            <div className="flex flex-col m-4 overflow-hidden min-w-[180px]">
              <Heading
                level={3}
                className="font-medium text-gray-900 truncate group highlight autoType400"
                data-testid="card-heading">
                {title}
              </Heading>
              <div className="text-gray-500 text autoType200">
                {'escapeLocation' in rental && (
                  <Text
                    component="div"
                    variant={ETextStyleVariant.MediumRegular}
                    // className="text-gray-50"
                    data-testid="card-info">
                    {rental.escapeLocation}
                  </Text>
                )}

                {rating && (
                  <Rating
                    data-testid="card-rating"
                    className="mr-1 font-bold text-green-500 autoType300"
                    number={rating.value}
                    iconSize={15}
                  />
                )}
              </div>
              <div className="flex mt-auto text-gray-900">
                {isUnavailable ? (
                  <span
                    className="text-gray-500 text text-200 leading-200"
                    data-testid="card-price">
                    {intl.formatMessage({
                      description: 'Price message for unavailable listing on search map',
                      defaultMessage: 'Not available for your dates',
                      id: 'kv+WHd',
                    })}
                  </span>
                ) : (
                  <Price
                    testId="card-price"
                    size={PriceSize.small}
                    showOriginalPrice={false}
                    currency={currency}
                    isMapPopup
                    {...price}
                  />
                )}
              </div>
            </div>
          </>
        </Link>
      </article>
    );
  }

  if (listingTileUpdateEnabled) {
    return (
      <GeneralPurposeListingTile
        isTransparent={false}
        listingTile={rental}
        showFirstImageOnly={!isAboveDesktop}
        hideFavoriteCount={isOnWishlist}
        wishlistingEventData={{ eventSource: EListingSource.WISHLIST_MAP_MARKER, index, page: 1 }}
        showGuestFavoriteTag
        showSuperhostBadge={superhostBadgeEnabled}
        target={isAboveDesktop ? '_blank' : undefined}
        onClick={() => onClickLink(source)}
        dataTestId={testId}
        className="w-80"
      />
    );
  }

  return (
    <>
      {isOnWishlist && !isGroup ? (
        <div className="w-full">
          <ModularListingTile
            key={`saved-rental-${index}`}
            rentalTile={rentalTile}
            target={isAboveDesktop ? '_blank' : undefined}
            onClick={() => onClickLink(source)}>
            {isAboveDesktop ? (
              <>
                <ModularListingTileMedia
                  rentalTile={rentalTile}
                  {...(rentalTile.loading ? {} : { largeSourceSize: false })}
                  className="aspect-[407/236] !h-full [&>div]:!pb-0 !rounded-none"
                  showFirstImageOnly={!isAboveDesktop}
                  wishlistingEventData={{
                    eventSource: EListingSource.WISHLIST_MAP_MARKER,
                    index: index ?? 0,
                    page: 1,
                  }}
                />
                <ModularListingTileContent rentalTile={rentalTile} className="p-4" />
              </>
            ) : (
              <div className="flex p-6 gap-4">
                <ModularSmallHorizontalListingTileMedia
                  rentalTile={rentalTile}
                  {...(rentalTile.loading ? {} : { argeSourceSize: false })}
                  className="w-20 h-20 min-w-[80px] min-h-[80px] aspect-square !rounded-2xl"
                  wishlistingEventData={{
                    eventSource: EListingSource.WISHLIST_MAP_MARKER,
                    index: index ?? 0,
                    page: 1,
                  }}
                />
                <ModularSmallHorizontalListingTileContent rentalTile={rentalTile} />
              </div>
            )}
          </ModularListingTile>
        </div>
      ) : (
        <article
          className={clsx({
            'w-80': (!isInstantBook && !hasDiscount) || (isInstantBook && !hasDiscount),
            'w-[350px]': !isInstantBook && hasDiscount,
            'w-[360px]': isInstantBook && hasDiscount,
          })}
          data-testid={testId}
          data-instantbook={String(isInstantBook)}
          data-unavailable={String(isUnavailable)}
          onClick={() => onClickLink(source)}>
          <Link
            naked
            aria-label={linkTitle}
            className="flex autoType400 hover:no-underline"
            href={href}
            target={isAboveDesktop ? '_blank' : undefined}
            variant={LinkVariants.noUnderline}
            title={linkTitle}>
            <>
              <div className="relative h-20 mr-2 overflow-hidden shrink-0 w-36 rounded-md">
                <ResponsiveImage
                  className="absolute w-full h-full top-1/2 left-1/2 transform -translate-y-1/2 -translate-x-1/2"
                  src={image.src}
                  role="presentation"
                  alt={image.alt}
                  sourceSizes={['landscape280']}
                  sizes="144px"
                />

                {superhostBadgeEnabled && isSuperhost && (
                  <SuperhostBadgeLightSvg className="absolute left-1 top-1" />
                )}
              </div>
              <div className="flex flex-col overflow-hidden">
                <Heading
                  level={3}
                  className="font-medium text-gray-900 truncate group highlight autoType400"
                  data-testid="card-heading">
                  {title}
                </Heading>
                <div className="text-gray-500 text autoType200">
                  {descriptionList.length > 0 && <InlineList list={descriptionList} />}
                  <div className="flex flex-wrap items-center">
                    {rating && (
                      <Rating
                        data-testid="card-rating"
                        className="mr-1 font-bold text-green-500 autoType200"
                        number={rating.value}
                        iconSize={10}
                      />
                    )}
                    <span data-testid="card-info">
                      <InlineList className={inlineListClasses} list={infoList} />
                    </span>
                  </div>
                </div>
                <div className="flex mt-auto text-gray-900">
                  {isUnavailable ? (
                    <span
                      className="text-gray-500 text text-200 leading-200"
                      data-testid="card-price">
                      {intl.formatMessage({
                        description: 'Price message for unavailable listing on search map',
                        defaultMessage: 'Not available for your dates',
                        id: 'kv+WHd',
                      })}
                    </span>
                  ) : (
                    <Price
                      testId="card-price"
                      size={PriceSize.small}
                      showOriginalPrice={false}
                      currency={currency}
                      isMapPopup
                      {...price}
                    />
                  )}
                </div>
              </div>
            </>
          </Link>
        </article>
      )}
    </>
  );
};

export default MapListing;
